"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { useRouter } from "next/navigation";

interface AppStartupErrorProps {
  error?: string;
  onRetry?: () => void;
}

/**
 * Safe fallback error screen shown when app fails to start
 * This prevents blank screens and crashes - always shows something useful
 */
export function AppStartupError({ error, onRetry }: AppStartupErrorProps) {
  const router = useRouter();

  const handleGoHome = () => {
    // Navigate to home page - this should always work
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default retry: reload the page
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-white">
      <Card padding="lg" className="w-full">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="rounded-full bg-red-100 p-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          
          <div className="space-y-2 w-full">
            <h2 className="text-xl font-semibold text-neutral-900">
              Unable to Start App
            </h2>
            <p className="text-sm text-neutral-600">
              {error || "Something went wrong while starting the app. Please try again."}
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full">
            {onRetry && (
              <Button
                onClick={handleRetry}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleGoHome}
              className="w-full"
            >
              <Home className="h-4 w-4 mr-2" />
              Go to Home
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
